export const SCORE_MIN = 0;
/** Legacy per-field cap (manual 0–3 scoring). Auto scores use computeAutoScores instead. */
export const SCORE_MAX = 3;
export const SCORE_FIELDS = [
  "evidence_score",
  "risk_score",
  "workload_score",
  "funding_compliance",
];

/** Max points: role(1) + staffing(4) + budget(45) + justification(50). */
export const AUTO_SCORE_MAX = 100;
export const JUSTIFICATION_SCORE_MAX = 50;
export const STAFFING_SCORE_MAX = 4;
export const BUDGET_SCORE_MAX = 45;

export const RATING_OPTIONS_1_TO_3 = [
  { value: 1, label: "1. Low risk" },
  { value: 2, label: "2. Moderate risk" },
  { value: 3, label: "3. High risk" },
];

export const EVIDENCE_RATING_OPTIONS_1_TO_3 = [
  { value: 1, label: "1. Low" },
  { value: 2, label: "2. Moderate" },
  { value: 3, label: "3. High" },
];

/**
 * Justification tab (max 50):
 * - Why role is needed (filled): 10
 * - Evidence strength rating 1–3: up to 20 (scaled)
 * - Risk rating 1–3: up to 15 (scaled)
 * - Merged/shared + explanation: 5
 */
export const computeJustificationScores = (form) => {
  const whyFilled = String(form?.why_role_is_needed || "").trim() !== "";
  const whyPoints = whyFilled ? 10 : 0;

  const evidenceRating = Number(form?.evidence_reference_score);
  const evidenceExplained = String(form?.evidence_reference || "").trim() !== "";
  const evidenceValid =
    !Number.isNaN(evidenceRating) &&
    evidenceRating >= 1 &&
    evidenceRating <= 3 &&
    evidenceExplained;
  const evidencePoints = evidenceValid
    ? Math.round((evidenceRating / 3) * 20)
    : 0;

  const riskRating = Number(form?.risk_if_role_is_not_available_score);
  const riskExplained = String(form?.risk_if_role_is_not_available || "").trim() !== "";
  const riskValid =
    !Number.isNaN(riskRating) &&
    riskRating >= 1 &&
    riskRating <= 3 &&
    riskExplained;
  const riskPoints = riskValid ? Math.round((riskRating / 3) * 15) : 0;

  const mergeAnswer = String(form?.can_this_role_be_merged_or_shared || "").trim();
  const mergeDetails = String(form?.merged_or_shared_details || "").trim();
  const mergeComplete =
    (mergeAnswer === "Yes" || mergeAnswer === "No") && mergeDetails !== "";
  const mergePoints = mergeComplete ? 5 : 0;

  const total = whyPoints + evidencePoints + riskPoints + mergePoints;

  const rows = [
    {
      key: "justification_why",
      criteria: "Justification — Why role is needed",
      detail: whyFilled ? "Provided" : "Not provided",
      points: whyPoints,
      maxPoints: 10,
    },
    {
      key: "justification_evidence",
      criteria: "Justification — Evidence strength (1–3)",
      detail: evidenceValid
        ? `Rated ${evidenceRating}/3`
        : "Rating and explanation required",
      points: evidencePoints,
      maxPoints: 20,
    },
    {
      key: "justification_risk",
      criteria: "Justification — Risk if role unavailable (1–3)",
      detail: riskValid
        ? `Rated ${riskRating}/3`
        : "Rating and explanation required",
      points: riskPoints,
      maxPoints: 15,
    },
    {
      key: "justification_merge",
      criteria: "Justification — Merged / shared",
      detail: mergeComplete
        ? `${mergeAnswer}: ${mergeDetails.slice(0, 80)}${
            mergeDetails.length > 80 ? "…" : ""
          }`
        : "Answer and explanation required",
      points: mergePoints,
      maxPoints: 5,
    },
  ];

  return {
    whyPoints,
    evidencePoints,
    riskPoints,
    mergePoints,
    total,
    rows,
  };
};

export const CRITICALITY_OPTIONS = [
  "Mission Critical",
  "Essential",
  "Important / Flexible",
  "Non-Critical / Optional",
];

/** Excel: =IF(AA="","",IF(AA>=75,"Mission Critical",IF(AA>=55,"Essential",IF(AA>=35,"Important / Flexible","Non-Critical / Optional")))) */
export const criticalityFromTotalScore = (totalScore) => {
  if (totalScore === null || totalScore === undefined || totalScore === "") {
    return "";
  }
  const score = Number(totalScore);
  if (Number.isNaN(score)) return "";
  if (score >= 75) return "Mission Critical";
  if (score >= 55) return "Essential";
  if (score >= 35) return "Important / Flexible";
  return "Non-Critical / Optional";
};

/**
 * Budget score (max 45).
 * No budget → 0. No gap or surplus (available ≥ total salary) → 45.
 * Shortfall tiers: &lt;10% → 35, 10–50% → 25, &gt;50% → 10.
 */
export const computeBudgetScore = (form) => {
  if (!form?.has_budget_for_role) return 0;
  const totalSalary = Number(form.total_salary);
  const available = Number(form.available_budget);
  if (
    form.total_salary === "" ||
    form.total_salary == null ||
    Number.isNaN(totalSalary) ||
    totalSalary <= 0
  ) {
    return 0;
  }
  if (Number.isNaN(available)) return 0;
  if (available >= totalSalary) return BUDGET_SCORE_MAX;

  const gapPct = ((totalSalary - available) / totalSalary) * 100;
  if (gapPct < 10) return 35;
  if (gapPct <= 50) return 25;
  return 10;
};

/**
 * Staffing Needs score from Stress gap (max 4).
 * Surplus/zero gap → 0. Larger shortage (more negative gap) → more marks, capped at 4.
 */
export const computeStressGapScore = (form) => {
  if (form?.minimum_gap_surplus === "" || form?.minimum_gap_surplus == null) {
    return 0;
  }
  const gap = Number(form.minimum_gap_surplus);
  if (Number.isNaN(gap) || gap >= 0) return 0;
  const shortage = Math.abs(gap);
  return Math.min(STAFFING_SCORE_MAX, Math.round(shortage));
};

const isStaffFieldFilled = (value) =>
  value !== "" && value !== null && value !== undefined;

/** Full wizard staffing step fills current + minimum (Yes path). */
export const isFullWizardStaffing = (form) =>
  isStaffFieldFilled(form?.current_staff_count) &&
  isStaffFieldFilled(form?.minimum_staff_needed);

/** True when current headcount has been captured (including 0). */
export const isCurrentHeadcountProvided = (form) =>
  isStaffFieldFilled(form?.current_staff_count);

/** Overall score above this → Critically needed = Yes. */
export const CRITICAL_NEED_SCORE_THRESHOLD = 70;

/** Score parts excluding the 0/1 critically-needed point (avoids circular dependency). */
export const computeBaseScoreWithoutRoleNeed = (form) => {
  const stressGapScore = computeStressGapScore(form);
  const budgetScore = computeBudgetScore(form);
  const justification = computeJustificationScores(form);
  return {
    stressGapScore,
    budgetScore,
    justification,
    total: stressGapScore + budgetScore + justification.total,
  };
};

/** True once staffing/budget/justification inputs exist (or a saved total score). */
export const hasWorkforceScoringInputs = (form) => {
  if (isStaffFieldFilled(form?.minimum_gap_surplus)) return true;
  if (form?.has_budget_for_role === true || form?.has_budget_for_role === false) {
    return true;
  }
  if (isStaffFieldFilled(form?.evidence_reference_score)) return true;
  if (isStaffFieldFilled(form?.risk_if_role_is_not_available_score)) return true;
  const saved = Number(form?.total_score);
  if (
    form?.total_score !== "" &&
    form?.total_score != null &&
    !Number.isNaN(saved)
  ) {
    return true;
  }
  return false;
};

const hasLiveScoringComponents = (form) => {
  if (isStaffFieldFilled(form?.minimum_gap_surplus)) return true;
  if (form?.has_budget_for_role === true || form?.has_budget_for_role === false) {
    return true;
  }
  if (isStaffFieldFilled(form?.evidence_reference_score)) return true;
  if (isStaffFieldFilled(form?.risk_if_role_is_not_available_score)) return true;
  return false;
};

/**
 * Critically needed rule:
 * - When scoring data exists: overall score > 70 → Yes
 * - Otherwise (create wizard step 0): use the explicit Yes/No radio
 */
export const resolveCriticallyNeedRole = (form) => {
  if (hasLiveScoringComponents(form)) {
    const { total } = computeBaseScoreWithoutRoleNeed(form);
    return total > CRITICAL_NEED_SCORE_THRESHOLD ? "yes" : "no";
  }

  const saved = Number(form?.total_score);
  if (
    form?.total_score !== "" &&
    form?.total_score != null &&
    !Number.isNaN(saved)
  ) {
    return saved > CRITICAL_NEED_SCORE_THRESHOLD ? "yes" : "no";
  }

  const explicit = String(form?.critically_need_role || "").trim().toLowerCase();
  if (explicit === "yes") return "yes";
  if (explicit === "no") return "no";

  return "";
};

export const computeRoleNeedScore = (form) =>
  resolveCriticallyNeedRole(form) === "yes" ? 1 : 0;

/**
 * Automatic scoring breakdown for the Scoring & Decision review table.
 * Maps into API fields: evidence_score (role), risk_score (stress),
 * funding_compliance (budget), workload_score (justification).
 */
export const computeAutoScores = (form) => {
  const base = computeBaseScoreWithoutRoleNeed(form);
  const resolvedNeed = resolveCriticallyNeedRole(form);
  const roleNeedScore = resolvedNeed === "yes" ? 1 : 0;
  const stressGapScore = base.stressGapScore;
  const budgetScore = base.budgetScore;
  const justification = base.justification;
  const total =
    roleNeedScore + stressGapScore + budgetScore + justification.total;
  const scaledForCriticality =
    AUTO_SCORE_MAX > 0 ? Math.round((total / AUTO_SCORE_MAX) * 100) : 0;

  const totalSalary = Number(form?.total_salary);
  const available = Number(form?.available_budget);
  let budgetGapPctLabel = "—";
  if (form?.has_budget_for_role && totalSalary > 0 && !Number.isNaN(totalSalary)) {
    const gapPct =
      !Number.isNaN(available) && available >= totalSalary
        ? 0
        : ((totalSalary - (Number.isNaN(available) ? 0 : available)) / totalSalary) * 100;
    budgetGapPctLabel = `${gapPct.toFixed(1)}%`;
  }

  const rows = [
    {
      key: "role_need",
      criteria: "Role & location — critically need this position",
      detail:
        resolvedNeed === "yes"
          ? `Overall score above ${CRITICAL_NEED_SCORE_THRESHOLD}`
          : `Overall score at or below ${CRITICAL_NEED_SCORE_THRESHOLD}`,
      points: roleNeedScore,
      maxPoints: 1,
    },
    {
      key: "stress_gap",
      criteria: "Staffing Needs — Stress gap (shortage)",
      detail:
        form?.minimum_gap_surplus === "" || form?.minimum_gap_surplus == null
          ? "Stress gap not set"
          : Number(form.minimum_gap_surplus) < 0
          ? `Shortage of ${Math.abs(Number(form.minimum_gap_surplus))} (more shortage = more marks)`
          : `Stress gap ${form.minimum_gap_surplus} (no shortage)`,
      points: stressGapScore,
      maxPoints: STAFFING_SCORE_MAX,
    },
    {
      key: "budget",
      criteria: "Budget — surplus / gap vs total salary",
      detail: !form?.has_budget_for_role
        ? "No budget for this role"
        : Number(form?.available_budget) >= Number(form?.total_salary) &&
          form?.total_salary !== ""
        ? "No gap / surplus — full budget marks"
        : `Budget gap ${budgetGapPctLabel} (of total salary)`,
      points: budgetScore,
      maxPoints: BUDGET_SCORE_MAX,
    },
    ...justification.rows,
  ];

  return {
    roleNeedScore,
    stressGapScore,
    budgetScore,
    justificationScore: justification.total,
    total,
    rows,
    evidence_score: roleNeedScore,
    risk_score: stressGapScore,
    workload_score: justification.total,
    funding_compliance: budgetScore,
    total_score: total,
    criticality_category: criticalityFromTotalScore(scaledForCriticality),
  };
};
export const COMMITTEE_DECISION_OPTIONS = [
  "Pending",
  "Approved",
  "Rejected",
  "Deferred",
  "More Information Required",
];
export const CD_APPROVAL_OPTIONS = [
  "Pending",
  "Approved",
  "Rejected",
  "Deferred",
  "More Information Required",
];

/**
 * Shared option lists for HR Sub-Committee review dropdowns + future charts.
 * Prefer these arrays everywhere (do not hardcode option strings in JSX).
 */
export const YES_NO_OPTIONS = ["Yes", "No"];

export const EVIDENCE_QUALITY_OPTIONS = ["Strong", "Medium", "Weak"];

export const SCENARIO_VALIDATION_OPTIONS = ["Validated", "Needs Audit"];

export const FUNDING_STATUS_OPTIONS = [
  "Funded",
  "Partially Funded",
  "Not Funded",
];

export const FUNDED_OR_ESCALATE_OPTIONS = ["Funded", "Escalate", "Deferred"];

export const HR_PROPOSED_CATEGORY_OPTIONS = [
  "Critical",
  "Essential",
  "Important / Flexible",
  "Non-Critical / Optional",
];

export const IMPLEMENTATION_STATUS_OPTIONS = [
  "Pending",
  "In Progress",
  "Completed",
  "Deferred",
];

/**
 * Committee review score formula (API field: `committe_review_score`).
 * Max total = 100 when all scored fields are at their highest marks.
 */
export const COMMITTEE_REVIEW_SCORE_FORMULA = [
  {
    field: "hr_sub_committee_evidence_quality",
    label: "Evidence quality",
    rules: "Strong = 10, Medium = 5, Weak/Low = 1",
  },
  {
    field: "hr_sub_committee_scenario_validation",
    label: "Scenario validation",
    rules: "Validated = 10, otherwise = 0",
  },
  {
    field: "funding_status",
    label: "Funding status",
    rules: "Funded = 15, Partially Funded = 5, Not Funded = 0",
  },
  {
    field: "can_merge_share_defer",
    label: "Can merge / share / defer",
    rules: "No = 10, Yes = 0",
  },
  {
    field: "stress_needed",
    label: "Stress needed",
    rules: "Yes = 10, No = 0",
  },
  {
    field: "risk_level_confirmed_high",
    label: "Risk level confirmed high",
    rules: "Yes = 15, No = 0",
  },
  {
    field: "evidence_clear",
    label: "Evidence clear",
    rules: "Yes = 10, No = 0",
  },
  {
    field: "cannot_cover_merge",
    label: "Cannot cover / merge",
    rules: "Yes = 10, No = 0",
  },
  {
    field: "funded_or_escalate",
    label: "Funded or escalate",
    rules: "Funded = 10, Escalate = 0, Deferred = 0",
  },
];

const COMMITTEE_REVIEW_SCORE_INPUT_FIELDS = new Set(
  COMMITTEE_REVIEW_SCORE_FORMULA.map((item) => item.field)
);

export const isCommitteeReviewScoreInputField = (fieldName) =>
  COMMITTEE_REVIEW_SCORE_INPUT_FIELDS.has(fieldName);

const normalizeScoreText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const scoreYesNo = (value, yesMarks = 10) =>
  normalizeScoreText(value) === "yes" ? yesMarks : 0;

/** Marks awarded when the answer is No (e.g. cannot merge/share/defer). */
const scoreNoYes = (value, noMarks = 10) =>
  normalizeScoreText(value) === "no" ? noMarks : 0;

/**
 * Recalculate `committe_review_score` from scored review fields.
 * Unscored inputs (e.g. current_staff_verified, budget_confirmed_until,
 * core_risk_linked_to) do not contribute marks.
 */
export const calculateCommitteeReviewScore = (form = {}) => {
  const evidence = normalizeScoreText(form.hr_sub_committee_evidence_quality);
  let evidenceMarks = 0;
  if (evidence === "strong") evidenceMarks = 10;
  else if (evidence === "medium") evidenceMarks = 5;
  else if (evidence === "weak" || evidence === "low") evidenceMarks = 1;

  const scenario = normalizeScoreText(form.hr_sub_committee_scenario_validation);
  const scenarioMarks = scenario === "validated" ? 10 : 0;

  const funding = normalizeScoreText(form.funding_status);
  let fundingMarks = 0;
  if (funding === "funded") fundingMarks = 15;
  else if (
    funding === "partially funded" ||
    funding === "partial funded" ||
    funding === "partially fundded"
  ) {
    fundingMarks = 5;
  }

  const fundedOrEscalate = normalizeScoreText(form.funded_or_escalate);
  const fundedOrEscalateMarks = fundedOrEscalate === "funded" ? 10 : 0;

  return (
    evidenceMarks +
    scenarioMarks +
    fundingMarks +
    scoreNoYes(form.can_merge_share_defer, 10) +
    scoreYesNo(form.stress_needed, 10) +
    scoreYesNo(form.risk_level_confirmed_high, 15) +
    scoreYesNo(form.evidence_clear, 10) +
    scoreYesNo(form.cannot_cover_merge, 10) +
    fundedOrEscalateMarks
  );
};

/**
 * Map committee review score → proposed category.
 * 75–100 Critical, 55–74 Essential, 35–54 Important / Flexible, 0–34 Non-Critical / Optional.
 */
export const COMMITTEE_REVIEW_SCORE_CATEGORY_BANDS = [
  {
    min: 75,
    max: 100,
    category: "Critical",
    meaning: "Highest priority — role is essential to mission delivery.",
  },
  {
    min: 55,
    max: 74,
    category: "Essential",
    meaning: "Strong need — role significantly supports operations.",
  },
  {
    min: 35,
    max: 54,
    category: "Essential under Stable / Deferred under Stress",
    meaning: "Role is essential to mission delivery, but can be deferred under stress.",
  },
  {
    min: 0,
    max: 34,
    category: "Non-Critical / Optional",
    meaning: "Lower priority — role can be deferred or covered elsewhere.",
  },
];

export const proposedCategoryFromCommitteeScore = (score) => {
  const n = Number(score);
  if (Number.isNaN(n)) return "Non-Critical / Optional";
  if (n >= 75) return "Critical";
  if (n >= 55) return "Essential";
  if (n >= 35) return "Important / Flexible";
  return "Non-Critical / Optional";
};

export const applyCommitteeReviewDerivedFields = (form = {}) => {
  const score = calculateCommitteeReviewScore(form);
  return {
    ...form,
    committe_review_score: String(score),
    hr_sub_committee_proposed_category: proposedCategoryFromCommitteeScore(score),
  };
};

export const getCommitteeReviewScoreBreakdown = (form = {}) =>
  COMMITTEE_REVIEW_SCORE_FORMULA.map((item) => {
    let marks = 0;
    switch (item.field) {
      case "hr_sub_committee_evidence_quality": {
        const v = normalizeScoreText(form.hr_sub_committee_evidence_quality);
        if (v === "strong") marks = 10;
        else if (v === "medium") marks = 5;
        else if (v === "weak" || v === "low") marks = 1;
        break;
      }
      case "hr_sub_committee_scenario_validation":
        marks =
          normalizeScoreText(form.hr_sub_committee_scenario_validation) ===
          "validated"
            ? 10
            : 0;
        break;
      case "funding_status": {
        const v = normalizeScoreText(form.funding_status);
        if (v === "funded") marks = 15;
        else if (
          v === "partially funded" ||
          v === "partial funded" ||
          v === "partially fundded"
        ) {
          marks = 5;
        }
        break;
      }
      case "can_merge_share_defer":
        marks = scoreNoYes(form.can_merge_share_defer, 10);
        break;
      case "stress_needed":
        marks = scoreYesNo(form.stress_needed, 10);
        break;
      case "risk_level_confirmed_high":
        marks = scoreYesNo(form.risk_level_confirmed_high, 15);
        break;
      case "evidence_clear":
        marks = scoreYesNo(form.evidence_clear, 10);
        break;
      case "cannot_cover_merge":
        marks = scoreYesNo(form.cannot_cover_merge, 10);
        break;
      case "funded_or_escalate":
        marks =
          normalizeScoreText(form.funded_or_escalate) === "funded" ? 10 : 0;
        break;
      default:
        marks = 0;
    }
    return {
      field: item.field,
      label: item.label,
      rules: item.rules,
      value: form[item.field] || "—",
      marks,
    };
  });

/** Field key → options array (for selects and chart axes). */
export const WORKFORCE_REVIEW_DROPDOWN_OPTIONS = {
  hr_sub_committee_evidence_quality: EVIDENCE_QUALITY_OPTIONS,
  current_staff_verified: YES_NO_OPTIONS,
  hr_sub_committee_scenario_validation: SCENARIO_VALIDATION_OPTIONS,
  funding_status: FUNDING_STATUS_OPTIONS,
  can_merge_share_defer: YES_NO_OPTIONS,
  stress_needed: YES_NO_OPTIONS,
  risk_level_confirmed_high: YES_NO_OPTIONS,
  evidence_clear: YES_NO_OPTIONS,
  cannot_cover_merge: YES_NO_OPTIONS,
  funded_or_escalate: FUNDED_OR_ESCALATE_OPTIONS,
  hr_sub_committee_proposed_category: HR_PROPOSED_CATEGORY_OPTIONS,
  implementation_status: IMPLEMENTATION_STATUS_OPTIONS,
};

/** Chart-ready dimension metadata (label + options). */
export const WORKFORCE_REVIEW_CHART_DIMENSIONS = [
  {
    key: "hr_sub_committee_evidence_quality",
    label: "Evidence quality",
    options: EVIDENCE_QUALITY_OPTIONS,
  },
  {
    key: "current_staff_verified",
    label: "Current staff verified",
    options: YES_NO_OPTIONS,
  },
  {
    key: "hr_sub_committee_scenario_validation",
    label: "Scenario validation",
    options: SCENARIO_VALIDATION_OPTIONS,
  },
  {
    key: "funding_status",
    label: "Funding status",
    options: FUNDING_STATUS_OPTIONS,
  },
  {
    key: "can_merge_share_defer",
    label: "Can merge / share / defer",
    options: YES_NO_OPTIONS,
  },
  {
    key: "stress_needed",
    label: "Stress needed",
    options: YES_NO_OPTIONS,
  },
  {
    key: "risk_level_confirmed_high",
    label: "Risk level confirmed high",
    options: YES_NO_OPTIONS,
  },
  {
    key: "evidence_clear",
    label: "Evidence clear",
    options: YES_NO_OPTIONS,
  },
  {
    key: "cannot_cover_merge",
    label: "Cannot cover / merge",
    options: YES_NO_OPTIONS,
  },
  {
    key: "funded_or_escalate",
    label: "Funded or escalate or deferred",
    options: FUNDED_OR_ESCALATE_OPTIONS,
  },
  {
    key: "hr_sub_committee_proposed_category",
    label: "Proposed category",
    options: HR_PROPOSED_CATEGORY_OPTIONS,
  },
  {
    key: "implementation_status",
    label: "Implementation status",
    options: IMPLEMENTATION_STATUS_OPTIONS,
  },
];

export const EMPTY_WORKFORCE_REVIEW_FORM = {
  review_id: "",
  hr_sub_committee_evidence_quality: "",
  current_staff_verified: "",
  minimum_staff_needed: "",
  stable_staff_needed: "",
  growth_staff_needed: "",
  hr_sub_committee_scenario_validation: "",
  funding_status: "",
  budget_confirmed_until: "",
  can_merge_share_defer: "",
  stress_needed: "",
  risk_level_confirmed_high: "",
  evidence_clear: "",
  core_risk_linked_to: "",
  cannot_cover_merge: "",
  funded_or_escalate: "",
  committe_review_score: "0",
  hr_sub_committee_technical_observation: "",
  hr_sub_committee_proposed_category: "",
  hr_sub_committee_recommendation: "",
  committee_justification: "",
  implementation_action: "",
  responsible: "",
  due_date: "",
  implementation_status: "Pending",
};

/** API field is misspelled as `committte_review` (3 t's). */
export const getCommitteeReviews = (row) => {
  const list = row?.committte_review ?? row?.committee_review ?? [];
  return Array.isArray(list) ? list : [];
};

export const getPrimaryCommitteeReview = (row) =>
  getCommitteeReviews(row)[0] || null;

export const hasCommitteeReview = (row) => Boolean(getPrimaryCommitteeReview(row)?.id);

const toDateInputValue = (value) => {
  if (!value) return "";
  const s = String(value).trim();
  if (s.length >= 10) return s.slice(0, 10);
  return s;
};

const toFormNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

export const reviewRecordToForm = (review, fallbackRow = null) => {
  let form;
  if (!review) {
    form = {
      ...EMPTY_WORKFORCE_REVIEW_FORM,
      minimum_staff_needed: toFormNumber(fallbackRow?.minimum_staff_needed),
      stable_staff_needed: toFormNumber(fallbackRow?.stable_staff_needed),
      growth_staff_needed: toFormNumber(fallbackRow?.growth_staff_needed),
      budget_confirmed_until: toDateInputValue(fallbackRow?.budget_confirmed_until),
      can_merge_share_defer: fallbackRow?.can_this_role_be_merged_or_shared || "",
    };
  } else {
    form = {
      ...EMPTY_WORKFORCE_REVIEW_FORM,
      review_id: review.id || "",
      hr_sub_committee_evidence_quality: review.hr_sub_committee_evidence_quality || "",
      current_staff_verified: review.current_staff_verified || "",
      minimum_staff_needed: toFormNumber(
        review.minimum_staff_needed ?? fallbackRow?.minimum_staff_needed
      ),
      stable_staff_needed: toFormNumber(
        review.stable_staff_needed ?? fallbackRow?.stable_staff_needed
      ),
      growth_staff_needed: toFormNumber(
        review.growth_staff_needed ?? fallbackRow?.growth_staff_needed
      ),
      hr_sub_committee_scenario_validation:
        review.hr_sub_committee_scenario_validation || "",
      funding_status: review.funding_status || "",
      budget_confirmed_until: toDateInputValue(
        review.budget_confirmed_until || fallbackRow?.budget_confirmed_until
      ),
      can_merge_share_defer:
        review.can_merge_share_defer ||
        fallbackRow?.can_this_role_be_merged_or_shared ||
        "",
      stress_needed: review.stress_needed || "",
      risk_level_confirmed_high: review.risk_level_confirmed_high || "",
      evidence_clear: review.evidence_clear || "",
      core_risk_linked_to: review.core_risk_linked_to || "",
      cannot_cover_merge: review.cannot_cover_merge || "",
      funded_or_escalate: review.funded_or_escalate || "",
      hr_sub_committee_technical_observation:
        review.hr_sub_committee_technical_observation || "",
      hr_sub_committee_recommendation: review.hr_sub_committee_recommendation || "",
      committee_justification: review.committee_justification || "",
      implementation_action: review.implementation_action || "",
      responsible: review.responsible || "",
      due_date: toDateInputValue(review.due_date),
      implementation_status: review.implementation_status || "Pending",
    };
  }

  return applyCommitteeReviewDerivedFields(form);
};

export const buildWorkforceReviewPayload = (workforcePlaningId, form) => {
  const toIntOrNull = (value) => {
    if (value === "" || value == null) return null;
    const n = parseInt(String(value), 10);
    return Number.isNaN(n) ? null : n;
  };

  const derived = applyCommitteeReviewDerivedFields(form);
  const payload = {
    workforce_planing_id: workforcePlaningId,
    hr_sub_committee_evidence_quality: form.hr_sub_committee_evidence_quality || "",
    current_staff_verified: form.current_staff_verified || "",
    minimum_staff_needed: toIntOrNull(form.minimum_staff_needed),
    stable_staff_needed: toIntOrNull(form.stable_staff_needed),
    growth_staff_needed: toIntOrNull(form.growth_staff_needed),
    hr_sub_committee_scenario_validation:
      form.hr_sub_committee_scenario_validation || "",
    funding_status: form.funding_status || "",
    budget_confirmed_until: form.budget_confirmed_until || "",
    can_merge_share_defer: form.can_merge_share_defer || "",
    stress_needed: form.stress_needed || "",
    risk_level_confirmed_high: form.risk_level_confirmed_high || "",
    evidence_clear: form.evidence_clear || "",
    core_risk_linked_to: form.core_risk_linked_to || "",
    cannot_cover_merge: form.cannot_cover_merge || "",
    funded_or_escalate: form.funded_or_escalate || "",
    committe_review_score: toIntOrNull(derived.committe_review_score) ?? 0,
    hr_sub_committee_technical_observation:
      form.hr_sub_committee_technical_observation || "",
    hr_sub_committee_proposed_category:
      derived.hr_sub_committee_proposed_category || "",
    hr_sub_committee_recommendation: form.hr_sub_committee_recommendation || "",
    committee_justification: form.committee_justification || "",
    implementation_action: form.implementation_action || "",
    responsible: form.responsible || "",
    due_date: form.due_date || "",
    implementation_status: form.implementation_status || "Pending",
  };

  if (form.review_id) {
    payload.id = form.review_id;
  }

  return payload;
};

export const ROLE_MERGED_OR_SHARED_OPTIONS = ["Yes", "No"];
export const DUTIES_COVERED_TEMPORARILY_OPTIONS = [
  "Yes",
  "No",
  "Partially",
  "Short time",
];

export const clampRating1To3 = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return Math.min(3, Math.max(1, Math.round(n)));
};

export const EMPTY_BUDGET_ENTRY = {
  project_name: "",
  budget_line: "",
  amount: "",
  budget_confirmed_until: "",
};

export const EMPTY_FORM = {
  role_id: "",
  department_id: "",
  office: "",
  area: "",
  critically_need_role: "",
  current_staff_count: "",
  minimum_staff_needed: "",
  stable_staff_needed: "",
  growth_staff_needed: "",
  minimum_gap_surplus: "",
  stable_gap_surplus: "",
  growth_gap_surplus: "",
  monthly_gross_salary: "",
  nbr_of_months: "",
  total_salary: "",
  has_budget_for_role: false,
  budget_details: [{ ...EMPTY_BUDGET_ENTRY }],
  available_budget: "",
  project_name: "",
  budget_line: "",
  budget_surplus_gap: "",
  budget_confirmed_until: "",
  why_role_is_needed: "",
  evidence_reference_score: "",
  evidence_reference: "",
  risk_if_role_is_not_available_score: "",
  risk_if_role_is_not_available: "",
  can_this_role_be_merged_or_shared: "",
  merged_or_shared_details: "",
  can_duties_be_covered_temporarily_by_another_role: "",
  evidence_score: "",
  risk_score: "",
  workload_score: "",
  funding_compliance: "",
  total_score: "",
  criticality_category: "",
  hod_requested_action_recommendation: "",
  committee_decision: "Pending",
  comments: "",
};

export const toInputDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

export const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return formatCell(value);
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = (value) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return value;
  }
};

const toNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? "" : n;
};

export const parseBudgetDetails = (record) => {
  let raw = record?.budget_details;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item) => ({
      project_name: item?.project_name || "",
      budget_line: item?.budget_line || "",
      amount: toNumberOrEmpty(item?.amount),
      budget_confirmed_until: toInputDate(item?.budget_confirmed_until),
    }));
  }
  if (record?.project_name || record?.budget_line || record?.budget_confirmed_until) {
    return [
      {
        project_name: record.project_name || "",
        budget_line: record.budget_line || "",
        amount: toNumberOrEmpty(record.available_budget),
        budget_confirmed_until: toInputDate(record.budget_confirmed_until),
      },
    ];
  }
  return [{ ...EMPTY_BUDGET_ENTRY }];
};

export const sumBudgetAmounts = (budgetDetails = []) => {
  const total = (budgetDetails || []).reduce((sum, item) => {
    const amount = Number(item?.amount);
    if (Number.isNaN(amount) || item?.amount === "" || item?.amount == null) {
      return sum;
    }
    return sum + amount;
  }, 0);
  return Math.round(total * 100) / 100;
};

export const isBudgetEntryComplete = (entry) => {
  if (!entry) return false;
  const amount = Number(entry.amount);
  return (
    String(entry.project_name || "").trim() !== "" &&
    String(entry.budget_line || "").trim() !== "" &&
    entry.amount !== "" &&
    entry.amount != null &&
    !Number.isNaN(amount) &&
    String(entry.budget_confirmed_until || "").trim() !== ""
  );
};

export const recordToForm = (record) => {
  if (!record) return { ...EMPTY_FORM, budget_details: [{ ...EMPTY_BUDGET_ENTRY }] };
  const budgetDetails = parseBudgetDetails(record);
  const hasBudget =
    Array.isArray(budgetDetails) &&
    budgetDetails.some(
      (item) =>
        item.project_name ||
        item.budget_line ||
        item.amount !== "" ||
        item.budget_confirmed_until
    );
  const form = {
    role_id: record.role_id || record.role?.id || "",
    department_id:
      record.department_id ||
      record.role?.department_id ||
      record.role?.department?.id ||
      record.department?.id ||
      "",
    office: record.office || "",
    area: record.area || "",
    critically_need_role: "",
    current_staff_count: toNumberOrEmpty(record.current_staff_count),
    minimum_staff_needed: toNumberOrEmpty(record.minimum_staff_needed),
    stable_staff_needed: toNumberOrEmpty(record.stable_staff_needed),
    growth_staff_needed: toNumberOrEmpty(record.growth_staff_needed),
    minimum_gap_surplus: toNumberOrEmpty(record.minimum_gap_surplus),
    stable_gap_surplus: toNumberOrEmpty(record.stable_gap_surplus),
    growth_gap_surplus: toNumberOrEmpty(record.growth_gap_surplus),
    monthly_gross_salary: toNumberOrEmpty(record.monthly_gross_salary),
    nbr_of_months: toNumberOrEmpty(record.nbr_of_months),
    total_salary: toNumberOrEmpty(record.total_salary),
    has_budget_for_role: hasBudget,
    budget_details: budgetDetails,
    available_budget: toNumberOrEmpty(record.available_budget),
    project_name: record.project_name || "",
    budget_line: record.budget_line || "",
    budget_surplus_gap: toNumberOrEmpty(record.budget_surplus_gap),
    budget_confirmed_until: toInputDate(record.budget_confirmed_until),
    why_role_is_needed: record.why_role_is_needed || "",
    evidence_reference_score: toNumberOrEmpty(record.evidence_reference_score),
    evidence_reference: record.evidence_reference || "",
    risk_if_role_is_not_available_score: toNumberOrEmpty(
      record.risk_if_role_is_not_available_score
    ),
    risk_if_role_is_not_available: record.risk_if_role_is_not_available || "",
    can_this_role_be_merged_or_shared: record.can_this_role_be_merged_or_shared || "",
    merged_or_shared_details: record.merged_or_shared_details || "",
    can_duties_be_covered_temporarily_by_another_role:
      record.can_duties_be_covered_temporarily_by_another_role || "",
    evidence_score: toNumberOrEmpty(record.evidence_score),
    risk_score: toNumberOrEmpty(record.risk_score),
    workload_score: toNumberOrEmpty(record.workload_score),
    funding_compliance: toNumberOrEmpty(record.funding_compliance),
    total_score: toNumberOrEmpty(record.total_score),
    criticality_category: record.criticality_category || "",
    hod_requested_action_recommendation: record.hod_requested_action_recommendation || "",
    committee_decision: record.committee_decision || "Pending",
    comments: record.comments || "",
  };
  form.critically_need_role = resolveCriticallyNeedRole(form) || "";
  return form;
};

const SKIP_PAYLOAD_KEYS = [
  "has_budget_for_role",
  "budget_details",
  "project_name",
  "budget_line",
  "budget_confirmed_until",
  "department_id",
  "critically_need_role",
];

const INT_FIELDS = [
  "current_staff_count",
  "minimum_staff_needed",
  "stable_staff_needed",
  "growth_staff_needed",
  "minimum_gap_surplus",
  "stable_gap_surplus",
  "growth_gap_surplus",
  "nbr_of_months",
];

const NUMBER_FIELDS = [
  "monthly_gross_salary",
  "total_salary",
  "available_budget",
  "budget_surplus_gap",
  "evidence_reference_score",
  "risk_if_role_is_not_available_score",
  "evidence_score",
  "risk_score",
  "workload_score",
  "funding_compliance",
  "total_score",
];

export const buildPayload = (form) => {
  const derived = withDerivedGapsAndScore(form);
  const payload = {};
  Object.keys(EMPTY_FORM).forEach((key) => {
    if (SKIP_PAYLOAD_KEYS.includes(key)) return;
    let value = derived[key];
    if (value === null || value === undefined) return;
    if (typeof value === "string") value = value.trim();
    if (value === "") return;

    if (INT_FIELDS.includes(key)) {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n)) payload[key] = n;
      return;
    }
    if (NUMBER_FIELDS.includes(key)) {
      const n = Number(value);
      if (Number.isNaN(n)) return;
      payload[key] = n;
      return;
    }
    payload[key] = value;
  });

  if (derived.has_budget_for_role) {
    const details = (derived.budget_details || [])
      .filter(isBudgetEntryComplete)
      .map((item) => ({
        project_name: String(item.project_name).trim(),
        budget_line: String(item.budget_line).trim(),
        amount: Number(item.amount),
        budget_confirmed_until: String(item.budget_confirmed_until).trim(),
      }));
    payload.budget_details = details;
    payload.available_budget = sumBudgetAmounts(details);

    const totalSalary = Number(payload.total_salary);
    const availableBudget = Number(payload.available_budget);
    if (!Number.isNaN(totalSalary) && !Number.isNaN(availableBudget)) {
      payload.budget_surplus_gap =
        Math.round((availableBudget - totalSalary) * 100) / 100;
    }
  } else {
    payload.budget_details = [];
    payload.available_budget = 0;
    delete payload.monthly_gross_salary;
    delete payload.nbr_of_months;
    delete payload.total_salary;
    delete payload.budget_surplus_gap;
  }

  return payload;
};

export const withDerivedGapsAndScore = (form) => {
  const next = {
    ...form,
    budget_details: Array.isArray(form.budget_details)
      ? form.budget_details.map((item) => ({ ...item }))
      : [{ ...EMPTY_BUDGET_ENTRY }],
  };

  const current = Number(form.current_staff_count);
  const min = Number(form.minimum_staff_needed);
  const stable = Number(form.stable_staff_needed);
  const growth = Number(form.growth_staff_needed);

  if (!Number.isNaN(current) && form.current_staff_count !== "") {
    if (!Number.isNaN(min) && form.minimum_staff_needed !== "") {
      next.minimum_gap_surplus = current - min;
    }
    if (!Number.isNaN(stable) && form.stable_staff_needed !== "") {
      next.stable_gap_surplus = current - stable;
    }
    if (!Number.isNaN(growth) && form.growth_staff_needed !== "") {
      next.growth_gap_surplus = current - growth;
    }
  }

  const monthly = Number(form.monthly_gross_salary);
  const months = Number(form.nbr_of_months);
  if (
    !Number.isNaN(monthly) &&
    form.monthly_gross_salary !== "" &&
    !Number.isNaN(months) &&
    form.nbr_of_months !== ""
  ) {
    next.total_salary = Math.round(monthly * months * 100) / 100;
  } else if (form.monthly_gross_salary === "" || form.nbr_of_months === "") {
    next.total_salary = "";
  }

  if (next.has_budget_for_role) {
    const sum = sumBudgetAmounts(next.budget_details);
    next.available_budget = sum;
    const first = next.budget_details.find(isBudgetEntryComplete) || next.budget_details[0];
    next.project_name = first?.project_name || "";
    next.budget_line = first?.budget_line || "";
    next.budget_confirmed_until = first?.budget_confirmed_until || "";
  } else {
    next.available_budget = 0;
  }

  const availableBudget = Number(next.available_budget);
  const totalSalary = Number(next.total_salary);
  if (!Number.isNaN(totalSalary) && next.total_salary !== "") {
    next.budget_surplus_gap =
      Math.round(((Number.isNaN(availableBudget) ? 0 : availableBudget) - totalSalary) * 100) /
      100;
  } else {
    next.budget_surplus_gap = "";
  }

  next.evidence_reference_score = clampRating1To3(next.evidence_reference_score);
  next.risk_if_role_is_not_available_score = clampRating1To3(
    next.risk_if_role_is_not_available_score
  );

  // Keep Yes/No in sync with overall score once scoring inputs exist (> 70 → Yes).
  // Leave the step-0 radio alone until scoring data is available.
  if (hasWorkforceScoringInputs(next)) {
    next.critically_need_role = resolveCriticallyNeedRole(next) || "";
  }

  const auto = computeAutoScores(next);
  next.evidence_score = auto.evidence_score;
  next.risk_score = auto.risk_score;
  next.workload_score = auto.workload_score;
  next.funding_compliance = auto.funding_compliance;
  next.total_score = auto.total_score;
  next.criticality_category = auto.criticality_category;

  return next;
};

export const criticalityColor = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("mission critical") || v === "critical") return "danger";
  if (v === "essential" || v === "high") return "warning";
  if (v.includes("important") || v === "medium") return "info";
  if (v.includes("non-critical") || v === "low" || v.includes("optional")) return "secondary";
  return "light";
};

export const decisionColor = (value) => {
  const v = String(value || "").toLowerCase();
  if (v === "approved") return "success";
  if (v === "rejected") return "danger";
  if (v === "pending") return "warning";
  if (v.includes("deferred") || v.includes("information")) return "info";
  return "secondary";
};

export const recordStatusLabel = (status) => {
  const code = Number(status);
  if (code === 10000) return "Draft";
  if (code === 10001) return "Completed";
  if (code === 10002) return "Cancelled";
  return formatCell(status);
};

export const recordStatusColor = (status) => {
  const code = Number(status);
  if (code === 10000) return "secondary";
  if (code === 10001) return "success";
  if (code === 10002) return "danger";
  return "light";
};

export const WORKFORCE_STATUS_OPTIONS = [
  { value: 10000, label: "Draft" },
  { value: 10001, label: "Completed" },
  { value: 10002, label: "Cancelled" },
];

/** Completed records (status 10001) cannot be edited. */
export const isWorkforcePlanningCompleted = (status) => Number(status) === 10001;
